import React, {useEffect, useState} from "react";
import {useParams, useNavigate, useLocation, Navigate} from "react-router-dom";
import {
    AthleteAdminBreadcrumbNav,
    AthleteAdminNavState,
    getTeamMemberBreadcrumbLabel,
    navigateToTeamMemberFromBreadcrumb,
    useAdminAthleteDisplayName,
    useAthleteStaffForAdminBreadcrumb,
} from "./AthleteAdminBreadcrumbNav";
import VideoLightbox from "./VideoLightbox";
import { courseVideoAnalytics, WORDPRESS_TAB } from "../services/courseEventService";
import CourseTipsModal from "./CourseTipsModal";
import CourseCompletionModal from "./CourseCompletionModal";
import ExpertModal from "./ExpertModal";
import ConnectFeaturedContentGrid from "./ConnectFeaturedContentGrid";
import {OutreachTemplatesSection} from "./OutreachTemplates";
import {isAthlete, isGuardian} from "../utils/auth";
import {athleteProfileFetchPath} from "../utils/viewedAthlete";
import {buildApiUrl} from "../utils/apiConfig";
import { buildCourseCompletionPayload, completeCourse } from "../services/courseTaskService";
import {useResourceVisibility} from "../hooks/useResourceVisibility";
import "../styles/athlete-admin-layout.css";
import "../styles/athlete-admin-scaled-wrapper.css";
import "../styles/connect.css";
import "../styles/course-cards.css";
import "../styles/expert.css";
import "../styles/pagination.css";
import "../styles/athlete-admin-page-spacing.css";
import { apiClient } from "../utils/apiClient";

interface Expert {
    id: number;
    slug: string;
    title: string;
    expertImageUrl: string;
    subtitle: string;
    excerpt: string;
    buttonLabel: string;
    buttonUrl: string;
    modalHeading: string;
    modalContent: string;
    modalCloseLabel: string;
}

interface Course {
    id: number;
    slug: string;
    title: string;
    courseImageUrl: string;
    subtitle: string;
    buttonLabel: string;
    buttonUrl: string;
    launchModalHeading: string;
    launchModalContent: string;
    launchModalButtonLabel: string;
    launchModalUrl: string;
    courseCompletionModalHeading: string;
    courseCompletionModalImageId: string;
    courseCompletionModalImageUrl: string;
    courseCompletionModalContent: string;
    courseCompletionModalBadgeId: string;
    courseCompletionModalBadgeUrl: string;
    courseCompletionModalDownloadUrl: string;
    courseCompletionModalCloseLabel: string;
    courseCompletionModalLinkText: string;
    courseCompletionModalIconId: string;
    courseCompletionModalIconUrl: string | null;
    expert: Expert | null;
    isCompleted: boolean;
}

interface CoachData {
    id?: string;
    firstName?: string;
    lastName?: string;
}

export default function ConnectPage() {
    const {athleteId} = useParams<{ athleteId?: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [courses, setCourses] = useState<Course[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentCoursePage, setCurrentCoursePage] = useState(1);
    const [coachData, setCoachData] = useState<CoachData | null>(null);
    const [selectedVideo, setSelectedVideo] = useState<{ id: number; title: string; vimeoUrl: string } | null>(null);
    const [isCourseTipsModalOpen, setIsCourseTipsModalOpen] = useState(false);
    const [pendingCourse, setPendingCourse] = useState<Course | null>(null);
    const [isCompletionModalOpen, setIsCompletionModalOpen] = useState(false);
    const [selectedExpert, setSelectedExpert] = useState<Expert | null>(null);
    const [isExpertModalOpen, setIsExpertModalOpen] = useState(false);
    const [athleteUsername, setAthleteUsername] = useState<string>("");

    // Check if current user is admin/staff viewing an athlete profile
    const isAdminViewingAthlete = !isAthlete() && !isGuardian() && Boolean(athleteId);
    const { visibility: resourceVisibility, loading: visibilityLoading } = useResourceVisibility();

    const getAthleteHubPath = () =>
        isAdminViewingAthlete && athleteId ? `/athlete/program/${athleteId}` : "/athlete/program";

    // Athletes see 2 courses per page, admins see 3
    const COURSES_PER_PAGE = 3;

    // Get team member info from navigation state
    const navState = location.state as AthleteAdminNavState | null;

    const athleteBreadcrumbName = useAdminAthleteDisplayName(athleteId, navState?.athleteDisplayName);

    const { staffId: athleteStaffId, staffName: athleteStaffName } = useAthleteStaffForAdminBreadcrumb(
        athleteId,
        Boolean(isAdminViewingAthlete && athleteId)
    );

    // Helper function to check if a URL is a video URL
    const isVideoUrl = (url: string | null): boolean => {
        if (!url) return false;
        const videoPatterns = [
            /vimeo\.com/i,
            /wistia\.com/i,
            /youtube\.com/i,
            /youtu\.be/i,
            /\.mp4$/i,
            /\.webm$/i,
            /\.ogg$/i
        ];
        return videoPatterns.some(pattern => pattern.test(url));
    };

    // Helper function to check if a URL is a downloadable document
    const isDocumentUrl = (url: string | null): boolean => {
        if (!url) return false;
        const documentPatterns = [
            /\.pdf$/i,
            /\.doc$/i,
            /\.docx$/i,
            /\.xls$/i,
            /\.xlsx$/i,
            /\.ppt$/i,
            /\.pptx$/i,
            /\.csv$/i,
            /\.txt$/i,
            /\.zip$/i,
            /\.rar$/i
        ];
        return documentPatterns.some(pattern => pattern.test(url));
    };

    // Handler for opening expert modal
    const handleLearnMore = (expert: Expert) => {
        setSelectedExpert(expert);
        setIsExpertModalOpen(true);
    };

    // Handler for course launch button clicks
    const handleCourseLaunch = (course: Course) => {
        if (!course.buttonUrl) return;

        // Show the tips modal and store the course
        setPendingCourse(course);
        setIsCourseTipsModalOpen(true);
    };

    // Handler for actually launching the course after tips modal
    const handleLaunchCourseAfterTips = () => {
        if (!pendingCourse || !pendingCourse.buttonUrl) return;

        console.log('📹 Launching course:', pendingCourse.title);
        console.log('📹 Button URL:', pendingCourse.buttonUrl);
        console.log('📹 Is video URL?', isVideoUrl(pendingCourse.buttonUrl));

        // Close the tips modal first
        setIsCourseTipsModalOpen(false);

        // If it's a video URL, open in lightbox
        if (isVideoUrl(pendingCourse.buttonUrl)) {
            console.log('📹 Opening video in lightbox with resourceId:', pendingCourse.id);
            setSelectedVideo({
                id: pendingCourse.id,
                title: pendingCourse.title,
                vimeoUrl: pendingCourse.buttonUrl
            });
        } else if (isDocumentUrl(pendingCourse.buttonUrl)) {
            // If it's a document, trigger download
            const link = document.createElement('a');
            link.href = pendingCourse.buttonUrl;
            link.download = '';
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            // Otherwise, open URL in new tab
            window.open(pendingCourse.buttonUrl, '_blank', 'noopener,noreferrer');
        }
    };

    // Handler for breadcrumb navigation to team member view
    const handleNavigateToTeamMember = () => {
        navigateToTeamMemberFromBreadcrumb(navigate, navState?.teamMemberId, athleteStaffId);
    };

    // Handler for when video ends
    const handleVideoEnd = (resourceId: number) => {
        const course = pendingCourse ?? courses.find((item) => item.id === resourceId) ?? null;
        if (course) {
            setCourses((prev) =>
                prev.map((item) => (item.id === course.id ? { ...item, isCompleted: true } : item)),
            );
            void completeCourse(buildCourseCompletionPayload(course)).catch((error) => {
                console.error("Failed to persist Connect course completion:", error);
            });
        }

        setSelectedVideo(null);
        setTimeout(() => {
            setIsCompletionModalOpen(true);
        }, 300);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                const headers: HeadersInit = {Accept: "application/json"};

                // Get team member info from navigation state if available
                if (navState?.teamMemberName) {
                    const nameParts = navState.teamMemberName.split(' ');
                    setCoachData({
                        id: navState.teamMemberId,
                        firstName: nameParts[0] || navState.teamMemberName,
                        lastName: nameParts.slice(1).join(' ') || '',
                    });
                }

                // Determine which athlete username/email to use (self, admin URL, or guardian-linked)
                let targetAthleteUsername = "";
                const profilePath = athleteProfileFetchPath(athleteId);
                if (profilePath) {
                    try {
                        const athleteRes = await apiClient(buildApiUrl(profilePath), {
                            headers,
                            credentials: "include",
                        });
                        if (athleteRes.ok) {
                            const athleteData = await athleteRes.json();
                            targetAthleteUsername = athleteData.username || athleteData.email || "";
                        }
                    } catch (meError) {
                        console.error("Failed to fetch athlete username:", meError);
                    }
                }
                setAthleteUsername(targetAthleteUsername);

                // Fetch courses from the Connect API
                try {
                    // Include username parameter to get completion status for athlete
                    const coursesUrl = targetAthleteUsername
                        ? `/api/connect/courses?username=${encodeURIComponent(targetAthleteUsername)}`
                        : `/api/connect/courses`;

                    const coursesRes = await apiClient(
                        buildApiUrl(coursesUrl),
                        {
                            headers,
                            credentials: "include" // Send HTTP-only cookies
                        }
                    );

                    if (coursesRes.ok) {
                        const coursesData = await coursesRes.json();

                        setCourses(Array.isArray(coursesData) ? coursesData : []);
                    } else {
                        console.warn("Could not fetch recruiting courses:", coursesRes.status);
                        if (coursesRes.status === 400) {
                            const errorData = await coursesRes.json().catch(() => ({}));
                            console.error("Backend error fetching recruiting courses:", errorData);
                        }
                        setCourses([]);
                    }
                } catch (coursesError) {
                    console.error("Error fetching recruiting courses:", coursesError);
                    setCourses([]);
                }
            } catch (error) {
                console.error("Failed to fetch recruiting data:", error);
                setError("Failed to load recruting data");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [athleteId, location.state]);

    // Pagination logic for courses
    const totalCoursePages = Math.ceil(courses.length / COURSES_PER_PAGE);
    const currentCourses = courses.slice(
        (currentCoursePage - 1) * COURSES_PER_PAGE,
        currentCoursePage * COURSES_PER_PAGE
    );

    const adminBreadcrumb = isAdminViewingAthlete ? (
        <AthleteAdminBreadcrumbNav
            className="connect-breadcrumbs-above-layout"
            pageLabel="Recruiting"
            teamMemberLabel={getTeamMemberBreadcrumbLabel(
                coachData,
                navState?.teamMemberName,
                athleteStaffName ?? undefined
            )}
            onTeamMemberClick={handleNavigateToTeamMember}
            athleteLabel={athleteBreadcrumbName}
            athleteProfileId={athleteId}
        />
    ) : null;

    if (visibilityLoading) {
        return (
            <div className="connect-page-wrapper">
                {adminBreadcrumb}
                <div className="connect-page-container">
                    <div className="loading-spinner">Loading...</div>
                </div>
            </div>
        );
    }

    // When the school disables recruiting resources, block direct URL/bookmark access.
    // Fail open when flags are unavailable (null) so content is never hidden by accident.
    if (resourceVisibility?.recruitingResourcesEnabled === false) {
        return <Navigate to={getAthleteHubPath()} replace />;
    }

    if (loading) {
        return (
            <div className="connect-page-wrapper">
                {adminBreadcrumb}
                <div className="connect-page-container">
                    <div className="loading-spinner">Loading...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="connect-page-wrapper">
                {adminBreadcrumb}
                <div className="connect-page-container">
                    <div className="error-message">{error}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="connect-page-wrapper">
            {adminBreadcrumb}

            <div className={`connect-page-container ${isAdminViewingAthlete ? 'with-athlete-sidebar' : ''}`}>
                    <section className="courses-section" data-testid="recruiting-courses">
                        <div className="courses-header">
                            <h2>Courses</h2>
                        </div>

                        {/* Expert Info and Courses Grid - Side by Side Layout */}
                        <div className="courses-content-layout">
                            {/* Courses Grid - Right Side */}
                            <div className="courses-grid" data-testid="courses-grid">
                                {currentCourses.map((course) => (
                                    <div key={course.id} className="course-card" data-testid={`course-${course.slug}`}>
                                        <div className="course-image">
                                            <img src={course.courseImageUrl} alt={course.title} />
                                        </div>
                                        <div className="course-content">
                                            {/* Expert info and completion badge row */}
                                            <div className="course-top-row">
                                                {course.expert && (
                                                    <div className="course-expert-info">
                                                        <div className="course-expert-avatar">
                                                            <img src={course.expert.expertImageUrl} alt={course.expert.title} />
                                                        </div>
                                                        <div className="course-expert-details">
                                                            <button
                                                                className="course-expert-name-link"
                                                                onClick={() => handleLearnMore(course.expert!)}
                                                                data-testid={`course-expert-link-${course.id}`}
                                                            >
                                                                {course.expert.title}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                                {course.isCompleted && (
                                                    <div className="course-completion-badge" data-testid={`course-completed-${course.id}`}>
                                                        <span className="completion-checkmark">✓</span>
                                                        <span className="completion-text">Complete</span>
                                                    </div>
                                                )}
                                            </div>

                                            <h4 className="course-name">{course.title}</h4>
                                            <button className="launch-btn" onClick={() => handleCourseLaunch(course)} data-testid={`course-action-${course.id}`}>
                                                {course.buttonLabel}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Pagination for Courses */}
                        {totalCoursePages > 1 && (
                            <div className="pagination" data-testid="pagination">
                                <div className="pagination-info">
                                    <span data-testid="pagination-info">
                                        Showing <strong>{(currentCoursePage - 1) * COURSES_PER_PAGE + 1}-{Math.min(currentCoursePage * COURSES_PER_PAGE, courses.length)}</strong> of <strong>{courses.length}</strong> courses
                                    </span>
                                </div>

                                <div className="pagination-controls">
                                    <div className="page-nav">
                                        <button
                                            className="page-btn"
                                            data-testid="prev-page-btn"
                                            onClick={() => setCurrentCoursePage(Math.max(currentCoursePage - 1, 1))}
                                            disabled={currentCoursePage <= 1}
                                            aria-label="Previous page"
                                        >
                                            Previous
                                        </button>
                                        {Array.from({ length: totalCoursePages }, (_, i) => i + 1).map((page) => (
                                            <button
                                                key={page}
                                                className={`page-number ${page === currentCoursePage ? "page-number-active" : ""}`}
                                                onClick={() => setCurrentCoursePage(page)}
                                                aria-label={`Page ${page}`}
                                                aria-current={page === currentCoursePage ? 'page' : undefined}
                                            >
                                                {page}
                                            </button>
                                        ))}
                                        <button
                                            className="page-btn"
                                            data-testid="next-page-btn"
                                            onClick={() => setCurrentCoursePage(Math.min(currentCoursePage + 1, totalCoursePages))}
                                            disabled={currentCoursePage >= totalCoursePages}
                                            aria-label="Next page"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </section>

                <section className="featured-resources-section" data-testid="recruiting-resources">
                    <h2>Resources</h2>
                    <ConnectFeaturedContentGrid/>
                </section>

                <OutreachTemplatesSection/>

                {/* Video Lightbox for course videos */}
                <VideoLightbox
                    open={!!selectedVideo}
                    resource={selectedVideo}
                    onClose={() => setSelectedVideo(null)}
                    onVideoEnd={handleVideoEnd}
                    analytics={selectedVideo ? courseVideoAnalytics(WORDPRESS_TAB.CONNECT, selectedVideo.id) : null}
                />

                {/* Course Tips Modal - for course launch tips */}
                <CourseTipsModal
                    isOpen={isCourseTipsModalOpen}
                    onClose={() => setIsCourseTipsModalOpen(false)}
                    onLaunchCourse={handleLaunchCourseAfterTips}
                    courseTitle={pendingCourse?.title}
                    modalHeading={pendingCourse?.launchModalHeading}
                    modalContent={pendingCourse?.launchModalContent}
                    modalButtonLabel={pendingCourse?.launchModalButtonLabel}
                />

                {/* Course Completion Modal - shown when video ends */}
                <CourseCompletionModal
                    isOpen={isCompletionModalOpen}
                    onClose={() => setIsCompletionModalOpen(false)}
                    modalHeading={pendingCourse?.courseCompletionModalHeading}
                    modalImageUrl={pendingCourse?.courseCompletionModalImageUrl}
                    modalContent={pendingCourse?.courseCompletionModalContent}
                    modalBadgeUrl={pendingCourse?.courseCompletionModalBadgeUrl}
                    modalDownloadUrl={pendingCourse?.courseCompletionModalDownloadUrl}
                    modalCloseLabel={pendingCourse?.courseCompletionModalCloseLabel}
                    modalLinkText={pendingCourse?.courseCompletionModalLinkText}
                    modalIconUrl={pendingCourse?.courseCompletionModalIconUrl}
                />

                {/* Expert Modal - shown when Learn More is clicked */}
                {isExpertModalOpen && selectedExpert && (
                    <ExpertModal
                        expert={selectedExpert}
                        onClose={() => {
                            setIsExpertModalOpen(false);
                            setSelectedExpert(null);
                        }}
                    />
                )}
            </div>
        </div>
    );
}
